import { defaultLanguage } from 'locales'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useNavigate } from 'react-router-dom'
import Iframe from 'shared/components/Iframe'
import { useIframe } from 'shared/hooks'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { AuthenticationProps } from './Authentication.types'

type EventDataMessage = {
  token?: string
  lang?: string
}

export const getRedirectOriginURL = (redirectOrigin: string): URL => {
  try {
    return new URL(redirectOrigin)
  } catch {
    return new URL(redirectOrigin, globalThis.location.origin)
  }
}

const CREDENTIALS_BASE_PATH = 'credentials/'

const Authentication = ({ props }: AuthenticationProps) => {
  const navigate = useNavigate()

  const { language } = useTranslation()

  const styleProps = useStyleMaps(props.styleMap)

  const waAuthenticationUrl = globalThis.waAuthenticationUrl

  const iframeSearchParams = new URLSearchParams()
  if (props.signUpPath) {
    iframeSearchParams.set('signUpPath', props.signUpPath)
  }

  if (props.recoverPasswordPath) {
    iframeSearchParams.set('recoverPasswordPath', props.recoverPasswordPath)
  }

  const { isModifyCredentials } = props

  const authenticationBasePath = isModifyCredentials
    ? CREDENTIALS_BASE_PATH
    : ''

  const { iframeAttributes, receiveMessage } = useIframe({
    origin: waAuthenticationUrl,
    src: `${waAuthenticationUrl}${authenticationBasePath}?${iframeSearchParams.toString()}`,
    title: 'Authentication'
  })

  const onReceivedMessage = useCallback(
    (event: MessageEvent) => {
      receiveMessage(event)

      const { token, lang: selectedLanguage }: EventDataMessage = event.data

      if (token) {
        const redirectOriginURL = getRedirectOriginURL(props.redirectOrigin)

        redirectOriginURL.searchParams.set('token', token)
        redirectOriginURL.searchParams.set(
          'lang',
          selectedLanguage || language || defaultLanguage
        )

        navigate({
          pathname: redirectOriginURL.pathname,
          search: redirectOriginURL.search
        })
      }
    },
    [language, navigate, props.redirectOrigin, receiveMessage]
  )

  useEffect(() => {
    globalThis.addEventListener('message', onReceivedMessage)
    return () => globalThis.removeEventListener('message', onReceivedMessage)
  }, [onReceivedMessage])

  if (!iframeAttributes) {
    return null
  }

  return <Iframe {...iframeAttributes} style={styleProps} />
}

export default Authentication
